import { useEffect, useRef, useState } from 'react';
import { Input, Text, Button } from '@fragments';
import { IconChevronDown, IconListDetails, IconLoader2, IconRefresh, IconTag, IconWebhook, IconX } from '@tabler/icons-react';
import Popper from '@fragments/Popper';
import api from '@helpers/api';
import { ACTION_TYPES_BY_PLATFORM, getDefaultConfigForType } from '../../../enums/actionTypes';
import './styles.scss';

function TagSelector({ action, integrationId, onUpdate }) {
    const popperRef = useRef();
    const [fetching, setFetching] = useState(false);
    const [cachedTags, setCachedTags] = useState([]);

    const selectedTagName = action.config?.tag_name || null;

    function buildOptions(tags) {
        return tags.map(tag => ({
            id: String(tag.id),
            label: tag.name,
            onClick: () => onUpdate({ config: { tag_id: String(tag.id), tag_name: tag.name } }),
        }));
    }

    async function fetchTags() {
        const res = await api.get(`integration/${integrationId}/activecampaign/getTagsAvailable`);
        return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
    }

    useEffect(() => {
        if (!integrationId) return;
        setFetching(true);
        fetchTags()
            .then(tags => setCachedTags(tags))
            .catch(e => console.error(e))
            .finally(() => setFetching(false));
    }, [integrationId]);

    async function handleOpen(event) {
        const anchor = event.currentTarget;
        if (fetching) return;

        popperRef.current.open(buildOptions(cachedTags), 'bottom', anchor);
    }

    async function handleRefresh(event) {
        const anchor = event.currentTarget;
        if (fetching) return;
        setFetching(true);
        try {
            const tags = await fetchTags();
            setCachedTags(tags);
            popperRef.current.open(buildOptions(tags), 'bottom', anchor);
        } catch (e) {
            console.error(e);
        } finally {
            setFetching(false);
        }
    }

    return (
        <div className="manage-action-card__selector-row">
            <Text.span left={<IconTag />} contrast={6} weight={400}>Tag:</Text.span>
            <div className="manage-action-card__selector-trigger">
                <Button
                    onClick={handleOpen}
                    color="overlay"
                    size="small"
                    disabled={fetching}
                    endIcon={fetching ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <IconChevronDown size={16} />}
                >
                    {selectedTagName ?? 'Selecionar tag'}
                </Button>
                <Button
                    onClick={handleRefresh}
                    icon={fetching ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <IconRefresh size={16} />}
                    color="overlay"
                    size="small"
                    className="ml-1"
                    disabled={fetching}
                    tooltip="Atualizar tags"
                />
            </div>
            <Popper
                controlRef={popperRef}
                config={{ searchKeyExtractor: (opt, validate) => validate(opt.label) }}
            />
        </div>
    );
}

function ListSelector({ action, integrationId, onUpdate }) {
    const popperRef = useRef();
    const [fetching, setFetching] = useState(false);
    const [cachedLists, setCachedLists] = useState([]);

    const selectedListName = action.config?.list_name || null;

    function buildOptions(lists) {
        return lists.map(list => ({
            id: String(list.id),
            label: list.name,
            onClick: () => onUpdate({ config: { list_id: String(list.id), list_name: list.name } }),
        }));
    }

    async function fetchLists() {
        const res = await api.get(`integration/${integrationId}/activecampaign/getListsAvailable`);
        return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
    }

    useEffect(() => {
        if (!integrationId) return;
        setFetching(true);
        fetchLists()
            .then(lists => setCachedLists(lists))
            .catch(e => console.error(e))
            .finally(() => setFetching(false));
    }, [integrationId]);

    function handleOpen(event) {
        const anchor = event.currentTarget;
        if (fetching) return;

        popperRef.current.open(buildOptions(cachedLists), 'bottom', anchor);
    }

    async function handleRefresh(event) {
        const anchor = event.currentTarget;
        if (fetching) return;
        setFetching(true);
        try {
            const lists = await fetchLists();
            setCachedLists(lists);
            popperRef.current.open(buildOptions(lists), 'bottom', anchor);
        } catch (e) {
            console.error(e);
        } finally {
            setFetching(false);
        }
    }

    return (
        <div className="manage-action-card__selector-row">
            <Text.span left={<IconListDetails />} contrast={6} weight={400}>Lista:</Text.span>
            <div className="manage-action-card__selector-trigger">
                <Button
                    onClick={handleOpen}
                    color="overlay"
                    size="small"
                    disabled={fetching}
                    endIcon={fetching ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <IconChevronDown size={16} />}
                >
                    {selectedListName ?? 'Selecionar lista'}
                </Button>
                <Button
                    onClick={handleRefresh}
                    icon={fetching ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <IconRefresh size={16} />}
                    color="overlay"
                    className="ml-1"
                    size="small"
                    disabled={fetching}
                    tooltip="Atualizar listas"
                />
            </div>
            <Popper
                controlRef={popperRef}
                config={{ searchKeyExtractor: (opt, validate) => validate(opt.label) }}
            />
        </div>
    );
}

export default function ActionCard({ action, platform, onRemove, onUpdate, integrationId }) {
    const typeOptions = ACTION_TYPES_BY_PLATFORM[platform] ?? [
        { value: 'webhook_call', label: 'Chamada de Webhook' },
    ];
    const isTypeLocked = typeOptions.length === 1;

    function handleTypeChange(val) {
        onUpdate({ type: val, config: getDefaultConfigForType(val) });
    }

    function handleUrlChange(val) {
        onUpdate({ config: { ...action.config, webhook_url: val } });
    }

    return (
        <div className="manage-action-card">
            <div className="manage-action-card__header">
                <Text.span contrast={8} weight={400}>
                    Tipo:
                </Text.span>

                <div className="manage-action-card__type-select">
                    <Input.Select
                        value={action.type}
                        onInput={handleTypeChange}
                        options={typeOptions}
                        disabled={isTypeLocked}
                        placeholder="Selecionar tipo de ação"
                    />
                </div>

                <Button
                    onClick={onRemove}
                    icon={<IconX size={16} />}
                    color="overlay"
                    size="small"
                    tooltip="Remover ação"
                />
            </div>

            {action.type === 'webhook_call' && (
                <div className="manage-action-card__body">
                    <Input.Text
                        label="URL de Webhook"
                        placeholder="Insira o endereço de webhook"
                        value={action.config?.webhook_url ?? ''}
                        onInput={handleUrlChange}
                        start={<IconWebhook size={16} />}
                    />
                </div>
            )}

            {action.type === 'tag_add' && (
                <div className="manage-action-card__body">
                    <TagSelector
                        action={action}
                        integrationId={integrationId}
                        onUpdate={onUpdate}
                    />
                </div>
            )}

            {action.type === 'list_add' && (
                <div className="manage-action-card__body">
                    <ListSelector
                        action={action}
                        integrationId={integrationId}
                        onUpdate={onUpdate}
                    />
                </div>
            )}
        </div>
    );
}
